import {
  effect,
  Inject,
  Injectable,
  makeStateKey,
  Optional,
  PLATFORM_ID,
  signal,
  TransferState,
  WritableSignal
} from '@angular/core';
import { Observable } from 'rxjs';
import { HttpProductService } from "./http/product.service";
import { isPlatformBrowser, isPlatformServer, Location } from "@angular/common";
import {
  ConfigurableProductVariantInterface,
  MatrixProductVariantInterface,
  Product,
  ProductFactory,
  ProductPageInterface,
  ProductPageResponseInterface,
  ProductVariantInterface,
  ShopEvents,
  SimpleProductVariantInterface
} from "@gea-waldviertler-website/gea-models";
import { BreadcrumbService, PageService } from "@gea-waldviertler-website/shop-lib";
import { RESPONSE } from "../../express.tokens";
import { Response } from "express";
import { MatomoService } from "./matomo.service";

const PRODUCT_CONTENT_KEY = makeStateKey<ProductPageResponseInterface>('product_content');

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  private productPageSignal: WritableSignal<ProductPageInterface | undefined | null> = signal(undefined)

  // mapcache
  private _cachedPageContents: Map<string, string>

  private storePickupEnabled: boolean = false

  constructor(
    @Inject(PLATFORM_ID) private platformId: typeof PLATFORM_ID,
    private transferState: TransferState,
    @Inject(HttpProductService) private http: HttpProductService,
    @Inject(PageService) private pageService: PageService,
    @Optional() @Inject(RESPONSE) private response: Response,
    private location: Location,
    @Inject(MatomoService) private matomoService: MatomoService,
    @Inject(BreadcrumbService) private bcService: BreadcrumbService,
  ) {
    this._cachedPageContents = new Map()

    // listen for shop event changes and change product page response if there are changed shop events
    effect(() => {
      const se = this.pageService.getActiveShopEventsSignal()()
      this.productPageSignal.update((cp) => {
        if (cp) {
          cp.product?.applyShopEvents(se)
        }
        let ccp: ProductPageInterface | undefined | null = cp ? { ...cp } : cp
        return ccp
      })
    })

    this.pageService.getConfigSubject().subscribe(config => {
      if (config) this.storePickupEnabled = config.storePickupEnabled
    })
  }

  /**
   * triggers a product page load. the response will be enriched by Shop Events and in future with stock info
   * and the enriched product will be written to the productPageSignal
   *
   * returns a observable which resolves to true on success and false on error
   * TODO: we should define an interface containing the error response on error
   */
  public loadProductPage(slug: string): Observable<number> {
    return new Observable<number>(subscriber => {
      this.getProduct(slug).subscribe(productPage => {
        const activeEvents = this.pageService.events()
        try {
          if (activeEvents) {
            productPage.product.applyShopEvents(activeEvents)
          } else {
            productPage.product.applyShopEvents(activeEvents)
          }
        } catch (e) {
          console.warn(e)
        }

        this.handleAliases(slug, productPage)
        // handle breadcrumbs
        const tree = this.bcService.trackProductPageView(productPage)
        // matomo: track product page view
        this.matomoService.trackProductPageView(productPage.product, true, tree)
        this.productPageSignal.set(productPage)
        subscriber.next(200)
      }, (error) => {
        console.warn(error)
        subscriber.next(typeof error?.status === 'number' ? error.status : 500)
        subscriber.complete()
      })
    })
  }

  private handleAliases(slug: string, productPage: ProductPageInterface): void {
    // if the product-slug coming from the api is different to the calling product-slug,
    // the api resolved the alias to the current product, so redirect
    if (slug !== productPage.product.slug) {
      if (! isPlatformBrowser(this.platformId) && this.response) {
        this.response.status(301)
        this.response.location('/shop/' + productPage.product.slug + '.html')
      }
      this.location.replaceState('/' + productPage.product.slug + '.html')
    }
  }

  public getProductPageSignal() {
    return this.productPageSignal.asReadonly()
  }

  public getProduct(slug: string) {
    // check cache
    if (this._cachedPageContents.has(slug)) {
      return new Observable<ProductPageInterface>(subscriber => {
        const contents = JSON.parse(<string>this._cachedPageContents.get(slug))
        let product = ProductFactory.getTypeInstance(contents.product)
        product.applyStock(contents.stock)
        subscriber.next({
          product: product,
          cmsPage: contents.cmsPage
        })
      })
    }

    let productObservable: Observable<ProductPageResponseInterface>

    // check ssr
    if (this.transferState.hasKey(PRODUCT_CONTENT_KEY)) {
      const contents = this.transferState.get<undefined | ProductPageResponseInterface>(PRODUCT_CONTENT_KEY, undefined)
      if (contents) {
        productObservable = new Observable<ProductPageResponseInterface>((subscriber) => {
          subscriber.next(contents)
        })
      }
      this.transferState.remove(PRODUCT_CONTENT_KEY)

      // fetch all
    } else {
      productObservable = this.http.getProduct(slug)
    }

    return new Observable<ProductPageInterface>(subscriber => {
      productObservable.subscribe(pageContents => {

        if (isPlatformServer(this.platformId)) {
          this.transferState.set(PRODUCT_CONTENT_KEY, pageContents)
        }

        this._cachedPageContents.set(slug, JSON.stringify(pageContents))
        let product = ProductFactory.getTypeInstance(pageContents.product)
        product.applyStock(pageContents.stock)
        const pi = {
          product: product,
          cmsPage: pageContents.cmsPage
        }
        subscriber.next(pi)
      }, error => {
        subscriber.error(error)
      })
    })
  }

  public splitByShopAction(product: Product<ProductVariantInterface>):
    Product<ProductVariantInterface>[] {
    if (! product.specialVariants) return [ product ]

    let splitProducts = []

    if (product.normalVariants && product.normalVariants.length) {
      // handle variants without shop action
      let normalProductData = product.toJSON()
      normalProductData.variants = []
      const variants = product.toJSON().variants
      for (const nvid of product.normalVariants) {
        for (const variant of variants) {
          if (variant.variantId == nvid) {
            if (product.canBeOrdered(this.storePickupEnabled, 1, variant.variantId)) {
              // TODO:  && product.isRemindPossible(variant as ProductVariantInterface)
              normalProductData.variants.push(variant)
            }
          }
        }
      }
      if (normalProductData.variants.length) {
        splitProducts.push(ProductFactory.getTypeInstance(normalProductData))
      }
    }

    // handle variants with shop action
    for (const [, psv] of Object.entries(product.specialVariants)) {
      let pd = product.toJSON()
      let specialProduct = ProductFactory.getTypeInstance(pd)
      if (specialProduct.typeId === 1) {
        specialProduct.variants = psv.variants as SimpleProductVariantInterface[]
      } else if (specialProduct.typeId === 2) {
        specialProduct.variants = psv.variants as ConfigurableProductVariantInterface[]
      } else if (specialProduct.typeId === 3) {
        specialProduct.variants = psv.variants as MatrixProductVariantInterface[]
      }

      specialProduct.specialPriceFrom = psv.priceFrom
      specialProduct.activeShopEvents = new ShopEvents()
      specialProduct.activeShopEvents.set(psv.shopEvent.id, psv.shopEvent)
      splitProducts.push(specialProduct)
    }

    return splitProducts
  }
}
